import cn from 'classnames'
import { requestSectionSettings } from 'legacy/lut/features/partner360/sectionSettings/api/requestSectionSettings'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { getUrlParam } from 'core-components/helpers/url'
import { IMetaInformation } from 'core-components/sitebuilder/models/IMetaInformation'
import { ExternalTileConfig, TileBoard } from 'partner360/components/tileBoard'
import {
  useWidgetTourActionHandler,
  useWidgetTourHostVisibility,
  WidgetTourAction,
  WidgetTourProvider,
} from 'partner360/components/widgetTour'
import { P360_PAGE_URL } from 'partner360/configs'
import { useWidgetTour } from 'partner360/features/widgetTour'
import { usePlanningViewPromoPending } from 'partner360/hooks/usePlanningViewPromoPending'
import { usePlanSectionsDockEnabled } from 'partner360/lib/planSectionsDockPreference'
import { NetworkDebugSource } from 'shared/lib/networkDebug'

import {
  setCockpitInitiativesView,
  setCockpitPartnerDetailsVisible,
  setCockpitRevenueAndSobView,
} from '../../../model'
import { useCockpitSwitchedOnHighlight } from '../../../model/hooks'
import {
  selectEffectiveCockpitFYear,
  selectFinancialCockpitDataRefreshVersion,
  selectIsCockpitOfferingSwitchedOff,
  selectIsFinancialCockpitLoading,
} from '../../../model/selectors'
import { requestFinancialCockpitData } from '../../../model/thunks'
import { useAttentionTileTone } from '../../cockpitAttentionStatuses'
import type { TDocumentType } from '../../cockpitDocumentsAndLinks/types'
import { useInitCockpitFYear } from '../../cockpitFiscalYear/hooks/useInitCockpitFYear'
import { CockpitPartnerDetails } from '../../cockpitPartnerDetails/CockpitPartnerDetails'
import {
  CockpitSalesTeam,
  CockpitSalesTeamExpanded,
  CockpitSalesTeamExpandedTitle,
  CockpitSalesTeamProvider,
} from '../../cockpitSalesTeam'
import { companySalesTeamTranslation as SALES_TEAM_KEYS } from '../../cockpitSalesTeam/translation'
import { CockpitScopePanel } from '../../cockpitScopePanel'
import { CockpitExportProvider } from '../../components/cockpitExport'
import { PlanningCockpitContentWrapper } from '../../planningCockpitContentWrapper'

import { buildTiles, COCKPIT_TILE_IDS } from './tiles'
import { PLANNING_COCKPIT_TOURS, PLANNING_COCKPIT_WIDGET_ID } from './tour/config'

import styles from './PlanningCockpit.module.scss'

/* Fitted to the screen, the Financial & SOB and Initiatives row is as tall as its figures and no
   taller; the rows below share what is left. */
const FIT_ROW_SIZES = ['auto']

interface IPlanningCockpitProps {
  pid?: string
}

type TTileViewAction =
  | ReturnType<typeof setCockpitRevenueAndSobView>
  | ReturnType<typeof setCockpitInitiativesView>

/** Which sub-view a tour step may open each tile onto. Only these tiles have sub-views, and a
 *  view a tile does not have leaves the step skipped rather than landing somewhere unrelated. */
const TILE_VIEW_SETTERS: Record<string, Record<string, TTileViewAction | undefined> | undefined> = {
  [COCKPIT_TILE_IDS.revenueAndSob]: {
    detail: setCockpitRevenueAndSobView('detail'),
    quality: setCockpitRevenueAndSobView('quality'),
  },
  [COCKPIT_TILE_IDS.initiatives]: {
    initiatives: setCockpitInitiativesView('initiatives'),
    mdf: setCockpitInitiativesView('mdf'),
    quality: setCockpitInitiativesView('quality'),
  },
}

const PlanningCockpitBody = ({ pid }: IPlanningCockpitProps) => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const salesTeamSourceRef = useRef<HTMLElement | null>(null)
  const salesTeamTitle = t(SALES_TEAM_KEYS.title)
  const externalTiles = useMemo<ExternalTileConfig[]>(
    () => [
      {
        id: COCKPIT_TILE_IDS.salesTeam,
        title: salesTeamTitle,
        subtitle: '',
        hideExpandButton: true,
        getExpandSource: () => salesTeamSourceRef.current,
        renderTitle: ({ onToggle }) => <CockpitSalesTeamExpandedTitle onCollapse={onToggle} />,
        renderExpanded: () => <CockpitSalesTeamExpanded />,
      },
    ],
    [salesTeamTitle]
  )
  const { pathname } = useLocation()
  const isPlanningCockpitActive = getUrlParam('page', pathname) === P360_PAGE_URL
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const isFYearResolved = useInitCockpitFYear('partner', Boolean(pid))
  const dataRefreshVersion = useSelector(selectFinancialCockpitDataRefreshVersion)
  const isCockpitLoading = useSelector(selectIsFinancialCockpitLoading)
  const attentionTone = useAttentionTileTone(false)
  const isSwitchedOff = useSelector(selectIsCockpitOfferingSwitchedOff)
  const isRefreshed = useCockpitSwitchedOnHighlight()
  const [documentsType, setDocumentsType] = useState<TDocumentType>()
  const tiles = useMemo(
    () =>
      buildTiles(pid, attentionTone, {
        isSwitchedOff,
        isRefreshed,
        documentsType,
        onDocumentsTypeChange: setDocumentsType,
      }),
    [attentionTone, isRefreshed, isSwitchedOff, pid, documentsType]
  )

  const [tourHostElement, setTourHostElement] = useState<HTMLDivElement | null>(null)
  const isTourHostVisible = useWidgetTourHostVisibility(tourHostElement)
  const isViewPromoPending = usePlanningViewPromoPending(pid ?? '')
  const [expandedTileId, setExpandedTileId] = useState<string | null>(null)

  const loadData = useCallback(
    ({
      keepSectionsLoaded = false,
      keepOnScreen = false,
    }: { keepSectionsLoaded?: boolean; keepOnScreen?: boolean } = {}) => {
      if (!pid || !isFYearResolved || !isPlanningCockpitActive) {
        return false
      }

      dispatch(
        requestFinancialCockpitData({
          fYear,
          partnerId: pid,
          keepSectionsLoaded,
          keepOnScreen,
          silent: keepOnScreen,
        })
      )
      // Load settings for the widgets below when the page opens directly with the cockpit,
      // without P360Application loading them through the legacy program-buttons widget.
      // TODO: After the full migration to cockpit, use PartnerData.SectionSettings from its
      // response instead of this separate request, keeping edits and refreshes in sync.
      dispatch(requestSectionSettings({ fYear, partnerId: pid }))

      return true
    },
    [dispatch, fYear, isFYearResolved, isPlanningCockpitActive, pid]
  )

  /* With the plan sections pinned under it the cockpit takes the screen; without, it keeps its
     three rows and the sections stand below. */
  const isFitToScreen = usePlanSectionsDockEnabled()
  const isFitToScreenRef = useRef(isFitToScreen)
  const lastRefreshVersionRef = useRef(dataRefreshVersion)
  /* The partner and year last loaded, to tell a year switch from a first load. */
  const loadedScopeRef = useRef<{ pid: string; fYear: number } | null>(null)

  isFitToScreenRef.current = isFitToScreen

  /* A refresh asked for after a save, as opposed to a new partner or year, lets the open plan
     section keep its data while the board reloads under it. Another year of the same partner
     loads with everything kept on screen: the open section, the deck and the board stay and
     their figures move to the new year's as it arrives. */
  useEffect(() => {
    const isRefresh = lastRefreshVersionRef.current !== dataRefreshVersion
    const loaded = loadedScopeRef.current
    const isYearSwitch = loaded !== null && loaded.pid === pid && loaded.fYear !== fYear

    lastRefreshVersionRef.current = dataRefreshVersion

    if (
      loadData({
        keepSectionsLoaded: isRefresh && isFitToScreenRef.current,
        keepOnScreen: isYearSwitch,
      }) &&
      pid
    ) {
      loadedScopeRef.current = { pid, fYear }
    }
  }, [dataRefreshVersion, fYear, loadData, pid])
  const reloadData = useCallback(() => {
    loadData()
  }, [loadData])

  /* The partner details stand in the cockpit's sidebar. Told from here rather than by the card,
     which the loader unmounts on every refresh: the Basic info section in the open dialog would
     otherwise rebuild and reload around each refresh. */
  useEffect(() => {
    if (!pid) {
      return
    }

    dispatch(setCockpitPartnerDetailsVisible(true))

    return () => {
      dispatch(setCockpitPartnerDetailsVisible(false))
    }
  }, [dispatch, pid])

  const handleTourAction = useCallback(
    (action: WidgetTourAction) => {
      switch (action.type) {
        case 'EXPAND_TILE': {
          if (![...tiles, ...externalTiles].some((tile) => tile.id === action.payload)) {
            return false
          }

          setExpandedTileId(action.payload)

          // The sub-view is dispatched after the expansion: the tile titles reset it to their
          // default when a tile is opened from the header, and although a tour expands through
          // the board's own state rather than that header, the order is not worth relying on.
          if (action.view) {
            const setView = TILE_VIEW_SETTERS[action.payload]?.[action.view]

            if (!setView) {
              return false
            }

            dispatch(setView)
          }

          return true
        }
        case 'COLLAPSE_TILE': {
          setExpandedTileId(null)
          return true
        }
        default:
          return false
      }
    },
    [dispatch, tiles, externalTiles]
  )

  useWidgetTourActionHandler(PLANNING_COCKPIT_WIDGET_ID, handleTourAction)

  const {
    hasTours,
    isLoading: isTourLoading,
    isTourActive,
    startTour,
  } = useWidgetTour(PLANNING_COCKPIT_WIDGET_ID, PLANNING_COCKPIT_TOURS, {
    isReady: !isCockpitLoading,
    // Not under the new-view promo: the tour waits until the card is closed.
    isAutoStartAllowed: isTourHostVisible && !isViewPromoPending,
  })

  if (!pid) {
    return null
  }

  return (
    <PlanningCockpitContentWrapper
      isBlurContent={false}
      isAdditionalLoading={!isFYearResolved}
      reloadData={reloadData}
    >
      <CockpitSalesTeamProvider
        partnerId={pid}
        expanded={expandedTileId === COCKPIT_TILE_IDS.salesTeam}
        onExpand={() => setExpandedTileId(COCKPIT_TILE_IDS.salesTeam)}
        onCollapse={() => setExpandedTileId(null)}
        sourceRef={salesTeamSourceRef}
      >
        {/* The tour host is the content itself: the wrapper unmounts it while the cockpit loads,
          which is what holds auto-start back until there is something to spotlight. */}
        <div
          ref={setTourHostElement}
          className={cn(styles.root, { [styles.rootFit]: isFitToScreen })}
          data-help-panel-host
        >
          <div className={styles.sidebar}>
            <CockpitPartnerDetails>
              <CockpitSalesTeam />
            </CockpitPartnerDetails>
          </div>
          <div className={styles.dashboard}>
            {/* The tour starts from the Help & tools panel; isTourHostVisible still holds auto-start
              back until the cockpit is on screen. */}
            <CockpitScopePanel
              partnerId={pid}
              fill={isFitToScreen}
              isTileExpanded={expandedTileId !== null}
              tour={{
                isAvailable: !isTourLoading && hasTours,
                isActive: isTourActive,
                onStart: startTour,
              }}
            >
              <CockpitExportProvider>
                <TileBoard
                  expandAnimation="stretch"
                  tiles={tiles}
                  externalTiles={externalTiles}
                  columns={4}
                  rows={isFitToScreen ? undefined : 3}
                  fitHeight={isFitToScreen}
                  rowSizes={isFitToScreen ? FIT_ROW_SIZES : undefined}
                  expandedTileId={expandedTileId}
                  onExpandedTileChange={setExpandedTileId}
                />
              </CockpitExportProvider>
            </CockpitScopePanel>
          </div>
        </div>
      </CockpitSalesTeamProvider>
    </PlanningCockpitContentWrapper>
  )
}

export const PlanningCockpit = (props: IPlanningCockpitProps) => (
  <NetworkDebugSource name="Cockpit">
    <WidgetTourProvider>
      <PlanningCockpitBody {...props} />
    </WidgetTourProvider>
  </NetworkDebugSource>
)

PlanningCockpit.displayName = 'PlanningCockpitWidget'

export const getMetaInfo = (): IMetaInformation => ({
  urlMapping: [{ propName: 'pid', isRequired: true }],
})
